/**
 * Life4Earth V2 — React hook for onboarding state.
 *
 * Loads the user's archetype, milestones, and computes unlocked features.
 * This hook is the single integration point for progressive disclosure
 * across the entire UI — LeftNav, DimensionRadar, route guards, etc.
 *
 * Pattern follows useApiImpactSignature.ts.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchOnboardingState, recordMilestone } from './onboarding';
import type { OnboardingStateResponse, RecordMilestoneRequest } from './onboarding';
import {
  getArchetypeConfig,
  computeUnlockedFeatures,
  getNextMilestone,
} from '../Data/Onboarding/archetypeConfig';
import type {
  ArchetypeKey,
  MilestoneKey,
  FeatureFlag,
  ArchetypeConfig,
  MilestoneUnlock,
} from '../Data/Onboarding/archetypeConfig';
import { fetchForKey, CACHE_UPDATE_EVENT } from './queryCache';

// ── Result shape ────────────────────────────────────────────

export interface UseApiOnboardingResult {
  /** Current archetype key (null if not yet onboarded). */
  archetype: ArchetypeKey | null;
  /** Full archetype config for the current archetype. */
  archetypeConfig: ArchetypeConfig | null;
  /** Whether onboarding wizard has been completed. */
  onboardingComplete: boolean;
  /** Set of achieved milestone keys. */
  achievedMilestones: Set<MilestoneKey>;
  /** Set of unlocked feature flags (baseline + milestone-unlocked). */
  unlockedFeatures: Set<FeatureFlag>;
  /** Next milestone to achieve (null if all done). */
  nextMilestone: MilestoneUnlock | null;
  /** Convenience: check if a specific feature is unlocked. */
  hasFeature: (flag: FeatureFlag) => boolean;
  /** Record a milestone and refresh state. */
  recordMilestone: (req: RecordMilestoneRequest) => Promise<void>;
  /** Profile provenance (§7.9): self_selected | invited | inferred | admin_assigned. */
  source: string | null;
  /** The inviting org community GUID (lineage edge), or null. */
  invitedCommunityGuid: string | null;
  /** Achievement timestamps by milestone key (e.g. the promotion Notice's
      Added date = when contributor_promoted fired). */
  milestoneDates: Record<string, string>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ── Hook ────────────────────────────────────────────────────

export function useApiOnboarding(
  userGuid: string | null | undefined
): UseApiOnboardingResult {
  const mountedRef = useRef(true);
  const lastFetchAtRef = useRef(0);
  const [rawState, setRawState] = useState<OnboardingStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = userGuid ? `onboarding:${userGuid}` : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;
    lastFetchAtRef.current = Date.now();

    try {
      const cached = await fetchForKey(
        cacheKey!,
        async () => fetchOnboardingState(userGuid),
        { force: true }
      );

      if (!mountedRef.current) return;
      setRawState(cached);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Failed to load onboarding state');
    }
  }, [userGuid, cacheKey]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) {
      // DEV-1443: on an identity switch, immediately drop the prior user's
      // state and show loading — never let a consumer derive archetype /
      // milestones from the previous user while the new fetch is in flight.
      // (The Explorer→Contributor promotion modal fired off a stale
      // 'contributor' archetype during the handover on shared browsers.)
      setRawState(null);
      setLoading(true);
      setError(null);
      refetch();
    } else {
      setRawState(null);
      setLoading(false);
      setError(null);
    }
    return () => { mountedRef.current = false; };
  }, [userGuid, refetch]);

  // Listen for cache invalidation (e.g. after SignalR milestone event)
  useEffect(() => {
    if (!cacheKey) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) {
        refetch();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [cacheKey, refetch]);

  // Refetch when the user returns to the tab / refocuses the window, so
  // admin-side changes (archetype, feature overrides) show up without a hard
  // reload — no SignalR needed. Throttled so tabbing in/out doesn't spam.
  useEffect(() => {
    if (!userGuid) return;
    const THROTTLE_MS = 30_000;
    const maybeRefetch = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastFetchAtRef.current < THROTTLE_MS) return;
      refetch();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefetch();
    };
    window.addEventListener('focus', maybeRefetch);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', maybeRefetch);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userGuid, refetch]);

  // Derived state
  const archetype = rawState?.archetype?.currentArchetype ?? null;
  const archetypeConfig = archetype ? getArchetypeConfig(archetype) : null;
  const onboardingComplete = rawState?.archetype?.onboardingComplete ?? false;

  const achievedMilestones = useMemo(() => {
    const set = new Set<MilestoneKey>();
    if (rawState?.milestones) {
      for (const m of rawState.milestones) {
        set.add(m.milestoneKey);
      }
    }
    return set;
  }, [rawState?.milestones]);

  const milestoneDates = useMemo(() => {
    const out: Record<string, string> = {};
    if (rawState?.milestones) {
      for (const m of rawState.milestones) {
        if (m.achievedAt) out[String(m.milestoneKey)] = m.achievedAt;
      }
    }
    return out;
  }, [rawState?.milestones]);

  const unlockedFeatures = useMemo(() => {
    if (!archetypeConfig) return new Set<FeatureFlag>();
    const features = computeUnlockedFeatures(archetypeConfig, achievedMilestones);
    // Apply admin manual overrides (feature_overrides): force a flag on/off.
    const overrides = rawState?.featureOverrides;
    if (overrides) {
      for (const [flag, on] of Object.entries(overrides)) {
        if (on) features.add(flag as FeatureFlag);
        else features.delete(flag as FeatureFlag);
      }
    }
    return features;
  }, [archetypeConfig, achievedMilestones, rawState?.featureOverrides]);

  const nextMilestone = useMemo(() => {
    if (!archetypeConfig) return null;
    return getNextMilestone(archetypeConfig, achievedMilestones);
  }, [archetypeConfig, achievedMilestones]);

  const hasFeature = useCallback(
    (flag: FeatureFlag) => unlockedFeatures.has(flag),
    [unlockedFeatures]
  );

  const doRecordMilestone = useCallback(
    async (req: RecordMilestoneRequest) => {
      if (!userGuid) return;
      await recordMilestone(userGuid, req);
      // Refresh state to pick up the new milestone
      refetch();
    },
    [userGuid, refetch]
  );

  return {
    archetype: archetype as ArchetypeKey | null,
    archetypeConfig,
    onboardingComplete,
    achievedMilestones,
    unlockedFeatures,
    nextMilestone,
    hasFeature,
    recordMilestone: doRecordMilestone,
    source: rawState?.source ?? null,
    invitedCommunityGuid: rawState?.invitedCommunityGuid ?? null,
    milestoneDates,
    loading,
    error,
    refetch,
  };
}
